'use client';

import {
  notFound,
  redirect,
  RedirectType,
  usePathname,
  useRouter,
  useSearchParams,
} from 'next/navigation';
import React, { useEffect } from 'react';
import NavBar from '~/components/NavBar';
import AppBootLoader from '~/components/Loading/AppBootLoader';
import { WorkspaceContentPane } from '~/components/Navigation/WorkspaceContentPane';
import { api } from '~/utils/api';
import defineAbilityFor from '~/utils/ability';
import { serverError } from '~/utils/serverError';

import { useUser } from '@auth0/nextjs-auth0/client';
import { Feature, Permission } from '@prisma/client';
import { Routes } from '~/config';
import { env } from '~/env.mjs';
import { ViewerBootstrapProvider } from '~/contexts/ViewerBootstrapContext';

const routeFeatureMapping: Record<Routes, Feature | null> = {
  [Routes.ASSISTANT]: null,
  [Routes.DOCUMENTS]: Feature.DOCUMENTS,
  [Routes.PROJECTS]: Feature.MARKETING_TASKS,
  [Routes.DECISION_STORE]: Feature.DECISION_STORE,
  [Routes.CLAIMS_APPROVAL]: Feature.CLAIMS_APPROVAL,
  [Routes.POLICY_ENGINE]: Feature.POLICY_ENGINE,
  [Routes.CONTENT_SCANNER]: Feature.CONTENT_SCANNER,
  [Routes.INFLUENCER_MONITORING]: Feature.INFLUENCER_MONITORING,
  [Routes.REMEDIATION]: Feature.REMEDIATION,
  [Routes.ARCHIVE_QUEUE]: Feature.ARCHIVE_QUEUE,
  [Routes.SETTINGS]: null,
  [Routes.COMMUNICATION_REVIEW]: Feature.COMMUNICATION_REVIEW,
  [Routes.NOTIFICATIONS]: null,
  [Routes.EMPLOYEE_REVIEW]: Feature.EMPLOYEE_REVIEW,
  [Routes.SELF_REPORTING]: Feature.EMPLOYEE_REVIEW,
};

const isDevBypass = env.NEXT_PUBLIC_DEV_AUTH_BYPASS === true;

const Wrapper = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const { user, isLoading: isAuth0Loading, error: auth0Error } = useUser();
  const {
    error: authorizeError,
    data: authorized,
    isLoading: isAuthorizeLoading,
  } = api.user.authorize.useQuery(undefined, {
    // Dev bypass: skip Auth0 user check — tRPC context uses local DB identity instead
    enabled: isDevBypass || !!user,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    onError: serverError,
  });

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const urlWorkspaceId = pathname?.split('/')[1];
  const needsWorkspaceResolution = urlWorkspaceId === 'null' || urlWorkspaceId === 'undefined';
  const shouldFetchUserWorkspaces =
    needsWorkspaceResolution && !isAuth0Loading && (isDevBypass || !!user);
  const { data: userWorkspaces, isLoading: isUserWorkspacesLoading, error: userWorkspacesError } =
    api.workspace.getUserWorkspaces.useQuery(undefined, {
      enabled: shouldFetchUserWorkspaces,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      onError: serverError,
    });

  useEffect(() => {
    const isInvalidWorkspace = urlWorkspaceId === 'null' || urlWorkspaceId === 'undefined';

    if (!isInvalidWorkspace || isUserWorkspacesLoading) {
      return;
    }

    const pathSegments = pathname?.split('/').slice(2) || [];
    const queryString = searchParams?.toString();
    const suffixPath = pathSegments.length > 0 ? `/${pathSegments.join('/')}` : '/';
    const suffix = `${suffixPath}${queryString ? `?${queryString}` : ''}`;

    if (userWorkspaces?.[0]?.workspaceId) {
      const validWorkspaceId = userWorkspaces[0].workspaceId;
      const newPath =
        pathSegments.length > 0
          ? `/${validWorkspaceId}/${pathSegments.join('/')}${queryString ? `?${queryString}` : ''}`
          : `/${validWorkspaceId}${queryString ? `?${queryString}` : ''}`;

      router.replace(newPath);
      return;
    }

    // Rescue failed: the workspace list is empty or the query errored (e.g. transient
    // 401 right after login). Don't strand the user on a 404 — defer to the server-side
    // workspace picker (workspace-redirect) as the single authority.
    if (userWorkspacesError || userWorkspaces !== undefined) {
      window.location.assign(
        `/api/workspace-redirect?returnTo=${encodeURIComponent(suffix)}`,
      );
    }
  }, [
    pathname,
    router,
    searchParams,
    urlWorkspaceId,
    userWorkspaces,
    userWorkspacesError,
    isUserWorkspacesLoading,
  ]);

  const route = ('/' + pathname?.split('/')[2]) as Routes;
  let isEnabled =
    !routeFeatureMapping[route] ||
    !!authorized?.user.tenant.enabledFeatures.includes(routeFeatureMapping[route]!);
  const ability = authorized
    ? defineAbilityFor(
        authorized.user.UserWorkspaces[0]?.Role?.isAdmin,
        authorized.user.UserWorkspaces[0]?.Role?.permissions,
      )
    : null;

  if (ability) {
    if (
      (!ability.can('do', Permission.CONTENT_SCANNER) && route === Routes.CONTENT_SCANNER) ||
      (!ability.can('do', Permission.VIEW_MARKETING_TASK) && route === Routes.PROJECTS) ||
      (!ability.can('do', Permission.INFLUENCER_MONITORING) &&
        route === Routes.INFLUENCER_MONITORING) ||
      (!ability.can('do', Permission.REMEDIATION_QUEUE) && route === Routes.REMEDIATION) ||
      (!ability.can('do', Permission.VIEW_POLICY_ENGINE) && route === Routes.POLICY_ENGINE)
    ) {
      isEnabled = false;
    }
  }

  const isLoading = isAuth0Loading || isAuthorizeLoading;
  const error = auth0Error || authorizeError;

  // TODO: better error handling
  // Handle specific case where user is authenticated but not provisioned in database
  if (
    error &&
    'data' in error &&
    error.data?.code === 'FORBIDDEN' &&
    error.message === 'USER_NOT_PROVISIONED'
  ) {
    // For SSO/SAML users (identified by having an org_id), redirect to the specific setup page
    if (user?.org_id) {
      const params = new URLSearchParams({
        email: user.email || '',
        provider: 'SSO',
      });
      return redirect(`/sso-setup-required?${params.toString()}`, RedirectType.replace);
    }
    // For regular users, redirect to access denied
    return redirect('/access-denied', RedirectType.replace);
  }

  // Handle other authentication errors — but not when dev bypass is active (no Auth0 session)
  if (!isDevBypass && ((!isAuth0Loading && !user) || error)) {
    const returnTo = encodeURIComponent(pathname || '/');
    return redirect(`/auth/login?returnTo=${returnTo}`, RedirectType.replace);
  }

  const isAppLoading =
    isLoading ||
    (needsWorkspaceResolution && (isUserWorkspacesLoading || !userWorkspaces?.[0]?.workspaceId));

  return (
    <AppBootLoader isPending={isAppLoading}>
      <ViewerBootstrapProvider authorized={authorized}>
        {isAppLoading ? null : !isEnabled ? (
          notFound()
        ) : (
          <div className="flex flex-col h-screen">
            {env.NEXT_PUBLIC_REMOTE_ENVIRONMENT_NAME === 'sandbox' && (
              <div className="h-[40px] bg-error flex items-center text-white justify-center font-semibold text-sm">
                SANDBOX MODE
              </div>
            )}
            {isDevBypass && (
              <div className="h-[40px] bg-orange-600 flex items-center text-white justify-center font-semibold text-sm">
                DEV AUTH BYPASS ACTIVE — not connected to Auth0
              </div>
            )}
            <div className="bg-shell flex h-full min-h-0 overflow-hidden">
              <NavBar />
              <div className="flex min-w-0 flex-1 flex-col overflow-hidden py-2 pr-2">
                <div
                  data-app-content-pane
                  className="bg-background relative min-h-0 flex-1 overflow-y-auto rounded-xl"
                >
                  <WorkspaceContentPane>{children}</WorkspaceContentPane>
                </div>
              </div>
            </div>
          </div>
        )}
      </ViewerBootstrapProvider>
    </AppBootLoader>
  );
};

function RootLayout({ children }: { children: React.ReactNode }) {
  return <Wrapper>{children}</Wrapper>;
}

export default RootLayout;
